import React, { useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Typography,
    Box,
    CircularProgress,
    IconButton,
    Tooltip,
    Paper
} from '@mui/material';
import { FileText, Download, AlertCircle } from 'lucide-react';
import { apiService } from '../services/api';

const SafetyLetterNotification = ({ letter, onSign }) => {
    const [open, setOpen] = useState(false);
    const [signature, setSignature] = useState('');
    const [signing, setSigning] = useState(false);
    const [error, setError] = useState('');
    const [viewAttempted, setViewAttempted] = useState(false);

    if (!letter) {
        return (
            <Alert severity="info">
                No safety letter available for the current month.
            </Alert>
        );
    }

    const handleSign = async () => {
        if (!viewAttempted) {
            setError('Please view the safety letter before signing');
            return;
        }

        if (!signature.trim()) {
            setError('Please provide your signature');
            return;
        }

        setSigning(true);
        try {
            await apiService.signSafetyLetter(letter.id, signature);
            setOpen(false);
            if (onSign) onSign();
        } catch (err) {
            console.error('Error signing safety letter:', err);
            setError('Failed to sign safety letter. Please try again.');
        } finally {
            setSigning(false);
        }
    };

    const handleView = () => {
        setViewAttempted(true);
        setError('');
        // Open PDF in new window
        if (letter.file_url) {
            window.open(letter.file_url, '_blank');
        } else {
            setError('Unable to open safety letter. Please try again or contact support.');
        }
    };

    const formatDate = (year, month) => {
        return new Date(year, month - 1).toLocaleString('default', { 
            month: 'long',
            year: 'numeric'
        });
    };

    return (
        <>
            <Alert 
                severity="info"
                icon={<AlertCircle />}
                action={
                    <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="View Safety Letter">
                            <IconButton
                                color="inherit"
                                size="small"
                                onClick={handleView}
                            >
                                <FileText size={20} />
                            </IconButton>
                        </Tooltip>
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => setOpen(true)}
                            startIcon={signing ? <CircularProgress size={20} /> : null}
                            disabled={signing}
                        >
                            Sign Now
                        </Button>
                    </Box>
                }
                sx={{ mb: 2 }}
            >
                <Typography variant="body1">
                    Monthly safety letter for {formatDate(letter.year, letter.month)} requires your signature
                </Typography>
            </Alert>

            <Dialog
                open={open}
                onClose={() => !signing && setOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    Safety Letter Signature Required
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" paragraph>
                        Please review and sign the monthly safety letter for {formatDate(letter.year, letter.month)}.
                    </Typography>

                    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f5f5f5' }}>
                        <Typography variant="subtitle2" gutterBottom>
                            Title: {letter.title}
                        </Typography>
                        <Button
                            variant="contained"
                            onClick={handleView}
                            startIcon={<Download size={16} />}
                            fullWidth
                            sx={{ mt: 1 }}
                        >
                            View Safety Letter
                        </Button>
                    </Paper>

                    <TextField
                        fullWidth
                        label="Electronic Signature"
                        value={signature}
                        onChange={(e) => setSignature(e.target.value)}
                        error={!!error}
                        helperText={error || "Type your full name as your electronic signature"}
                        disabled={signing}
                        sx={{ mt: 2 }}
                    />

                    <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                        By signing, you confirm that you have read and understood the safety letter.
                        Your signature will be recorded with a timestamp and IP address for compliance purposes.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={() => setOpen(false)} 
                        disabled={signing}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSign} 
                        variant="contained"
                        disabled={signing || !signature.trim() || !viewAttempted}
                        startIcon={signing ? <CircularProgress size={20} /> : null}
                    >
                        {signing ? 'Signing...' : 'Sign & Submit'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default SafetyLetterNotification;